/**
 * Normalisation pour la recherche — LE fichier partagé entre le client et
 * le serveur.
 *
 * C'est ce partage qui fait tout l'intérêt : le serveur stocke
 * `catalogItems.search = normalise(nom + alias)` et le client filtre en
 * mémoire avec la même fonction. Deux implémentations séparées finiraient
 * par diverger, et le symptôme serait le pire possible — « la recherche ne
 * trouve pas la même chose hors-ligne qu'en ligne ».
 */

/**
 * Sans accent, sans ponctuation, en minuscules.
 *
 * `\p{Diacritic}` après une décomposition NFD règle é→e, à→a, ç→c… mais PAS
 * les ligatures : « œuf » ne se décompose pas en « oeuf ». On les traite
 * donc explicitement, sinon taper « oeuf » ne trouve jamais « œuf » — qui
 * est probablement le mot le plus tapé de toute l'application.
 */
export function normalise(s: string): string {
  return s
    .normalize('NFD')
    .replace(/\p{Diacritic}/gu, '')
    .toLowerCase()
    .replace(/œ/g, 'oe')
    .replace(/æ/g, 'ae')
    .replace(/[''\-_/.,()]/g, ' ')
    .replace(/\s+/g, ' ')
    .trim()
}

/** Colonne `search` d'une ligne de catalogue : le nom plus tous ses alias. */
export function colonneRecherche(nom: string, alias: readonly string[] = []): string {
  return normalise([nom, ...alias].join(' | '))
}

/**
 * Radical : on retire le pluriel pour comparer.
 *
 * « Pains burger » et « Pain burger » ne se rencontraient jamais, pas plus
 * que « Oignons rouges » et « Oignon rouge » — et c'est exactement ce que
 * l'assistant écrit quand il détaille une recette. On ne touche PAS à la
 * colonne stockée : le radical ne sert qu'à la comparaison, des deux côtés,
 * ce qui évite toute migration.
 *
 * Seuls les mots de 4 lettres ou plus sont réduits : « os » ou « as » ne
 * doivent pas perdre leur dernière lettre. « pois » devient « poi » des
 * deux côtés, donc l'égalité tient.
 */
export function radical(s: string): string {
  return s
    .split(' ')
    .map((m) => (m.length >= 4 ? m.replace(/[sx]$/, '') : m))
    .join(' ')
}

/** Ce qui, juste après un nom de produit, annonce un produit DIFFÉRENT
 *  plutôt qu'une variante. Voir le barème dans `score`. */
const PREPOSITIONS = /^(de |d |a |au |aux |en |pour |sans )/

/** Vrai si le mot qui précède la position donnée n'est pas une préposition. */
function sansPreposition(avant: string): boolean {
  const dernier = avant.trim().split(' ').pop() ?? ''
  return !PREPOSITIONS.test(`${dernier} `)
}

/** Mots de liaison, retirés seulement pour tester l'ÉGALITÉ de deux noms. */
const LIAISONS = new Set(['de', 'du', 'des', 'd', 'a', 'au', 'aux', 'la', 'le', 'les', 'l'])

/**
 * Le même nom, débarrassé de ses mots de liaison intérieurs.
 *
 * « Pains à burger » et *Pains burger* désignent le même produit et ne se
 * rencontraient pas. Retirer les liaisons DES DEUX CÔTÉS ne peut pas créer
 * de faux positif : le nombre de mots pleins reste différent dès que les
 * produits le sont — « lait coco » ne devient jamais « lait ». C'est pour
 * ça que ça ne s'applique qu'au test d'égalité, jamais à l'inclusion.
 */
function sansLiaison(s: string): string {
  const mots = s.split(' ').filter((m, i) => i === 0 || !LIAISONS.has(m))
  return mots.join(' ')
}

export type Trouvable = {
  readonly nom: string
  readonly search: string
  /** 0 = produit de base (lait, œufs), 1 = courant, 2 = rare. */
  readonly tier: number
}

/**
 * Score de pertinence, du plus fort au plus faible.
 *
 * DEUX SENS DE COMPARAISON, et c'est le point que la première version
 * ratait complètement. Elle ne testait que le cas où la saisie est PLUS
 * COURTE que le nom du catalogue (« tomate » → *Tomates*). Or un assistant
 * qui détaille une recette écrit plus PRÉCIS que le catalogue : « Parmesan
 * râpé », « Poivre noir », « Pavés de saumon ». Sept de ces cas sur huit
 * scoraient zéro, donc restaient en texte libre, donc comptaient comme
 * manquants alors que le produit était en stock.
 *
 * L'ordre des barèmes n'est pas décoratif : la correspondance par ALIAS
 * (55) passe avant l'inclusion de mots (52), pour que « sauce tomate »
 * trouve *Coulis de tomate*, qui la porte en alias, plutôt que *Tomates*,
 * qui n'est pas la même chose.
 *
 * Retourne 0 quand il n'y a pas de correspondance.
 */
export function score(entree: Trouvable, aiguilleNormalisee: string): number {
  const q = radical(aiguilleNormalisee)
  if (q === '') return 0

  const nomExact = normalise(entree.nom)
  const nom = radical(nomExact)
  const hay = radical(entree.search)

  /*
   * L'ÉGALITÉ STRICTE PASSE AVANT L'ÉGALITÉ AU PLURIEL PRÈS, et l'absence
   * de cette distinction a coûté cher.
   *
   * `radical` retire la finale des mots de quatre lettres et plus. Une fois
   * les accents tombés, « pâtes » et « pâté » donnent donc le MÊME radical
   * — « pate » — et les deux produits scoraient 100 sur la même requête.
   * Lequel gagnait relevait de l'ordre du catalogue.
   *
   * Le symptôme vécu : une fusion de doublons a absorbé « Spaghetti Ebro »
   * dans *Pâté* au lieu de *Pâtes*. Le pâté a hérité du synonyme et du
   * code-barres des spaghettis, et taper « pâtes » tombait dessus. Une
   * erreur de barème qui écrit dans les données est bien pire qu'une
   * erreur d'affichage : elle survit à sa correction.
   */
  if (nomExact === aiguilleNormalisee) return 100
  // Le nom exact, au pluriel près.
  if (nom === q) return 98
  if (sansLiaison(nom) === sansLiaison(q)) return 95
  /*
   * UN ALIAS EXACT PASSE AVANT UN DÉBUT DE NOM, et l'ordre inverse coûtait
   * cher : « patates » tombait sur *Patate douce* — dont le nom commence
   * bien par « patate » une fois le pluriel retiré — au lieu de *Pommes de
   * terre*, qui le déclare comme synonyme.
   *
   * Un alias est une AFFIRMATION : le foyer, ou la graine, a écrit que ce
   * mot désigne ce produit. Un début de nom n'est qu'une ressemblance
   * typographique. L'affirmation bat l'inférence.
   */
  if (hay.split(' | ').some((a) => a === q)) return 90
  // La saisie est le début du nom : « tomate » → Tomates.
  if (nom.startsWith(q)) return 80
  /*
   * Le nom est le début de la saisie : Parmesan ← « Parmesan râpé ».
   *
   * MAIS PAS si ce qui suit est une préposition. « X adjectif » est une
   * variante du même produit — parmesan râpé, poivre noir, persil frais.
   * « X de Y » désigne au contraire un AUTRE produit : lait de coco n'est
   * pas du lait, beurre de cacahuète n'est pas du beurre. Confondre les
   * deux ferait croire qu'on a l'un quand on a l'autre.
   */
  if (q.startsWith(nom + ' ') && !PREPOSITIONS.test(q.slice(nom.length + 1))) return 70
  // Début d'un mot du nom : « ecreme » → Lait demi-écrémé.
  if (nom.includes(` ${q}`)) return 60
  // Le début d'un alias. Le `|` sépare les alias dans `search`.
  if (hay.includes(`| ${q}`)) return 55
  /*
   * Le nom apparaît en mots entiers DANS la saisie — mais jamais derrière
   * une préposition.
   *
   * Même règle qu'au barème 70, dans l'autre sens : « jus de citron » n'est
   * pas des citrons, « sachet de sauce » n'est pas de la sauce. Sans ce
   * garde-fou, le jus de citron se rattachait aux *Citrons* et comptait
   * comme en stock quand on avait des citrons entiers.
   *
   * Ça coûte des rattachements légitimes — « pavés de saumon » ne trouvera
   * pas *Saumon* par ce chemin — et c'est le bon arbitrage : un faux
   * rattachement est silencieux et fausse le verdict « ai-je tout ? » dans
   * le sens rassurant, là où une absence est visible et se corrige. Les
   * cas perdus ressortent de toute façon en PISTES, via `proches`.
   */
  if (nom.length >= 4) {
    const avantNom = (fin: number) => sansPreposition(q.slice(0, fin))
    const i = q.indexOf(` ${nom} `)
    if (i >= 0 && avantNom(i + 1)) return 52
    if (q.endsWith(` ${nom}`) && avantNom(q.length - nom.length)) return 52
  }
  if (hay.includes(q)) return 30
  return 0
}

/**
 * Recherche complète, en mémoire.
 *
 * Balayage linéaire assumé : le catalogue fait quelques centaines d'entrées,
 * donc moins d'une milliseconde, sans index à maintenir ni dépendance.
 * Et c'est obligatoirement du JavaScript, puisque la recherche qui compte
 * — celle du supermarché — doit fonctionner hors-ligne.
 */
export function chercher<T extends Trouvable>(entrees: readonly T[], aiguille: string, limite = 12): T[] {
  const q = normalise(aiguille)
  if (q === '') return []

  const notes: Array<{ e: T; s: number }> = []
  for (const e of entrees) {
    const s = score(e, q)
    if (s > 0) notes.push({ e, s })
  }

  notes.sort((a, b) => b.s - a.s || a.e.tier - b.e.tier || a.e.nom.localeCompare(b.e.nom, 'fr'))
  return notes.slice(0, limite).map((n) => n.e)
}

/**
 * Les noms du catalogue les plus PROCHES — volontairement laxiste, là où
 * `score` est volontairement strict.
 *
 * Les deux fonctions ne servent pas le même but, et les confondre était un
 * défaut de conception. `score` décide d'un RATTACHEMENT : une erreur y est
 * silencieuse et fausse le verdict « ai-je tout ? » dans le sens
 * rassurant, donc le doute doit se résoudre par un refus. `proches` ne
 * décide rien, elle SUGGÈRE à un humain ou à l'assistant : une erreur n'y
 * coûte qu'une ligne ignorée, et un silence coûte un aller-retour perdu.
 *
 * Concrètement : « Semoule de couscous épicé » ne se RATTACHE pas à
 * *Semoule* — « de » annonce peut-être un autre produit — mais *Semoule*
 * doit évidemment être PROPOSÉE.
 */
export function proches<T extends Trouvable>(
  entrees: readonly T[],
  aiguille: string,
  limite = 3,
): T[] {
  const mots = radical(normalise(aiguille))
    .split(' ')
    .filter((m) => m.length >= 3)
  if (mots.length === 0) return []

  return entrees
    .map((e) => {
      const hay = radical(e.search)
      // Un mot compte double s'il ouvre le nom du produit : « semoule » dans
      // *Semoule* pèse plus que « épicé » perdu dans un alias.
      const nom = radical(normalise(e.nom))
      let poids = 0
      for (const m of mots) {
        if (nom === m || nom.startsWith(`${m} `)) poids += 2
        else if (hay.includes(m)) poids += 1
      }
      return { e, poids }
    })
    .filter((r) => r.poids > 0)
    .sort((a, b) => b.poids - a.poids || a.e.tier - b.e.tier || a.e.nom.length - b.e.nom.length)
    .slice(0, limite)
    .map((r) => r.e)
}
